import { api } from "../../lib/api/api";
import { path } from "../../lib/path/path";
import { args } from "../../lib/args/args";
import { CliError } from "../../lib/cli/cli-error";
import { expectShape } from "../../lib/expect/expect-shape";
import { is } from "../../lib/is/is";
import { out } from "../../lib/output/out";
import {
  ACTIONS,
  REJECT_MODES,
} from "@/commands/approvals/approvals-constants";
import { listApprovals } from "@/commands/approvals/list-approvals";
import type { ApiOutputs } from "@/lib/api/api-outputs";
import type { ParsedArgs } from "@/lib/parsed/parsed-args";

/**
 * Approving and rejecting require a human session and are refused for an API
 * key: a machine standing in for human validation would defeat the guardrail.
 */
export async function approvalsCommand(parsed: ParsedArgs): Promise<void> {
  const action = args.action(parsed, "approvals", ACTIONS, "list");
  const asJson = parsed.booleans.has("json");

  if (action === "list") {
    args.expect(parsed, { positional: 1 });
    return await listApprovals(asJson);
  }

  args.expect(parsed, {
    positional: 2,
    ...(action === "reject" ? { flags: ["mode", "feedback"] } : {}),
  });
  const guardrailId = args.required(parsed, 1, "guardrailRequestId");

  if (action === "show") {
    const detail = expectShape<ApiOutputs["approvals"]["get"]>(
      await api(path`/approvals/${guardrailId}`),
      "GET /approvals/{id}",
      { guardrailKind: is.string, status: is.string, summary: is.string },
    );
    if (asJson) {
      out.json(detail);
      return;
    }
    out.line(
      `${out.paint(out.cell(detail.guardrailKind), "bold")}  ${detail.status}`,
    );
    out.line(out.safe(detail.summary));
    out.line();
    out.json(detail.payload);
    return;
  }

  if (action === "approve") {
    const result = await api<ApiOutputs["approvals"]["approve"]>(
      path`/approvals/${guardrailId}/approve`,
      { method: "POST", body: {} },
    );
    if (asJson) {
      out.json(result);
      return;
    }
    out.line(`${out.paint("✓", "green")} Approved ${out.cell(guardrailId)}`);
    return;
  }

  const mode = args.choice(parsed, "mode", REJECT_MODES) ?? "rework";
  if (mode === "rework" && !parsed.flags.feedback) {
    throw new CliError(
      "--feedback is required with --mode rework: the agent needs to know what to change.",
      2,
    );
  }

  const result = await api<ApiOutputs["approvals"]["reject"]>(
    path`/approvals/${guardrailId}/reject`,
    {
      method: "POST",
      body: {
        mode,
        ...(parsed.flags.feedback ? { feedback: parsed.flags.feedback } : {}),
      },
    },
  );
  if (asJson) {
    out.json(result);
    return;
  }
  out.line(
    `${out.paint("✓", "green")} Rejected ${out.cell(guardrailId)} (${mode})`,
  );
}
